import { api } from '../api/client';

import { API_API_KEYS_URL } from '../utils';

/**
 * API keys: a user-owned, scoped credential for machine clients (scripts,
 * agents, other tools). A key acts AS the user — it can do at most what the
 * user can do, in the collections it is allowed into — and is never admin.
 *
 * The raw token (`kpr_…`) comes back exactly once, in the create response;
 * only its hash is stored, so the listing can never show it again. Someone
 * who loses a key revokes it and makes another.
 *
 * Every route here is session-only on the server: a key cannot list, mint,
 * or revoke keys. The web app always calls with the session JWT, so that is
 * invisible from here.
 */

/**
 * Scope choices, as the create form offers them. `write` implies `read`.
 * Neither touches the card surface: that is the separate `cards` scope
 * below, a flag beside the radio rather than a third radio, because it
 * combines with either — a key can design cards without writing items.
 */
export const SCOPE_OPTIONS = [
	{ value: 'read', label: 'Read only' },
	{ value: 'write', label: 'Read and write' },
];

/** The `cards` scope's control, off by default: card definitions, element
 *  sets and card layouts are the shape of a person's data, and the ordinary
 *  "let my assistant log things" key has no business redesigning it. */
export const CARDS_SCOPE_LABEL = 'Can change cards';
export const CARDS_SCOPE_NOTE =
	'Lets this key create and change cards, element sets and layouts in its collections. '
	+ 'Leave off unless the assistant is meant to redesign your data.';

/**
 * The `delete` scope's control, off by default and never implied (keepr-api
 * docs/SCHEMA.md § API keys; the delete audit's F14). It rides BESIDE write
 * or cards — it lets a key remove what it could already change — so the
 * switch is only live when one of those is on, and the API refuses it
 * without them. Keys made before DELETE_SCOPE_SINCE do not have it and are
 * not grandfathered: deleting from a tool needs a new key.
 */
export const DELETE_SCOPE_LABEL = 'Can delete records';
export const DELETE_SCOPE_NOTE =
	'Lets this key delete items, attachments, relationships and saved setup in its collections — '
	+ 'up to a daily limit. Only you can restore a deleted item, from History. '
	+ 'Leave off unless the tool is meant to remove things.';
export const DELETE_SCOPE_NEEDS_WRITE = 'Needs Read and write or Can change cards: a key can only delete what it can change.';
export const DELETE_SCOPE_SINCE = '2026-09-24';
export const PRE_DELETE_SCOPE_NOTICE =
	'Keys created before 24 September 2026 cannot delete records. To let a tool delete, create a new key with Can delete records on.';

/** Whether Can delete records may be on: it needs write or cards beside it. */
export function deleteScopeAllowed(scope = 'read', cards = false) {
	const base = Array.isArray(scope) ? scope : [scope];
	return base.includes('write') || Boolean(cards);
}

/**
 * What goes on the wire for `scopes`: the radio's own value when neither
 * flag is on (a string, as the API has always accepted), the array once
 * `cards` or `delete` joins it. The API normalises either into its stored
 * order. `delete` is dropped when nothing it could ride on is chosen — the
 * form disables the switch then, and the API would refuse it anyway.
 */
export function scopesPayload(scope = 'read', cards = false, canDelete = false) {
	const base = Array.isArray(scope) ? scope : [scope];
	const withDelete = canDelete && deleteScopeAllowed(base, cards);
	if (!cards && !withDelete) return scope;
	const out = base.filter(s => s !== 'cards' && s !== 'delete');
	if (cards) out.push('cards');
	if (withDelete) out.push('delete');
	return out;
}

/**
 * True for a key minted before the `delete` scope existed that therefore
 * lacks it — the rows the panel's one-line notice is about.
 */
export function keyPredatesDeleteScope(key) {
	if (!key?.createdAt) return false;
	const scopes = Array.isArray(key.scopes) ? key.scopes : [];
	if (scopes.includes('delete')) return false;
	return new Date(key.createdAt).getTime() < Date.parse(`${DELETE_SCOPE_SINCE}T00:00:00Z`);
}

/**
 * Expiry choices. `null` is "never" — the API applies no default, so a key
 * with no `expiresIn` lives until it is revoked.
 */
export const EXPIRY_OPTIONS = [
	{ value: null, label: 'Never' },
	{ value: '30d', label: '30 days' },
	{ value: '90d', label: '90 days' },
	{ value: '1y', label: '1 year' },
];

export const ONE_TIME_TOKEN_NOTE =
	"Copy this key now — you won't see it again. If it's lost, revoke it and make a new one.";

/** The server's cap on active keys per user (400 over it). */
export const MAX_ACTIVE_KEYS = 25;

/**
 * The caller's keys, newest first — metadata only, never a token. Revoked
 * rows come back only when asked for.
 */
export async function listApiKeys({ includeRevoked = false } = {}) {
	const { data } = await api.get(API_API_KEYS_URL, {
		params: includeRevoked ? { includeRevoked: true } : undefined,
	});
	return Array.isArray(data) ? data : [];
}

/**
 * 201 → { key: {…meta}, token: 'kpr_…' } — the ONLY response that carries
 * the token.
 *
 * Only what is set goes up: `collectionIds` is omitted for "all my
 * collections" (the API refuses an empty array), `expiresIn` is omitted for
 * "never".
 */
export async function createApiKey({ name, scopes = 'read', cards = false, canDelete = false, collectionIds, expiresIn } = {}) {
	const payload = { name: (name || '').trim(), scopes: scopesPayload(scopes, cards, canDelete) };
	if (Array.isArray(collectionIds) && collectionIds.length) payload.collectionIds = collectionIds;
	if (expiresIn) payload.expiresIn = expiresIn;
	const { data } = await api.post(API_API_KEYS_URL, payload);
	return data;
}

/** Revoke — immediate, idempotent. The row stays listed under "show revoked". */
export async function revokeApiKey(id) {
	const { data } = await api.delete(`${API_API_KEYS_URL}/${id}`);
	return data;
}

/** 'active' | 'expired' | 'revoked', from the row's timestamps. */
export function apiKeyStatus(key, now = Date.now()) {
	if (key?.revokedAt) return 'revoked';
	if (key?.expiresAt && new Date(key.expiresAt).getTime() <= now) return 'expired';
	return 'active';
}

/**
 * The list's one-phrase reading of a key's scopes: 'Read', 'Read and write',
 * 'Read + cards' or 'Read, write and cards' — each with 'and delete' when
 * the key can delete. Read is always there (the API never stores a key
 * without it), so it is never a separate word.
 */
export function apiKeyScopeLabel(key) {
	const scopes = Array.isArray(key?.scopes) ? key.scopes : [];
	const write = scopes.includes('write');
	const cards = scopes.includes('cards');
	if (scopes.includes('delete') && (write || cards)) {
		const parts = ['Read', write ? 'write' : null, cards ? 'cards' : null].filter(Boolean);
		return `${parts.join(', ')} and delete`;
	}
	if (write && cards) return 'Read, write and cards';
	if (cards) return 'Read + cards';
	if (write) return 'Read and write';
	return 'Read';
}
